import { FC, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { channelPartnerApi } from 'src/api/newChannelPartner/channelPartner';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { InputField } from 'src/components/input-field';
import { pincodeApi } from 'src/api/pincode';
import { BasicProfile, ChannelPartner } from 'src/interfaces/new-channel-partner/channelPartner';
import { emailRegex, phoneRegExp, pincodeRegex } from 'src/components/common/common-regex';
import { MenuProps } from 'src/components/masters/customCss';
import { typeOfFirms } from '../../../../enums/common';

interface EditBasicInfoProps {
  open: boolean;
  onClose: () => void;
  channelPartnerDetails: ChannelPartner;
  updateBasicDetails: (data: BasicProfile) => Promise<boolean>;
}

export const EditBasicInfo: FC<EditBasicInfoProps> = (props) => {
  const { open, onClose, channelPartnerDetails, updateBasicDetails } = props;
  const [checkPincode, setCheckPincode] = useState<boolean>(false);
  const [isEmailExists, setIsEmailExist] = useState<boolean>(false);
  const stringRegExp = /^[A-Za-z ]+$/;
  const [areas, setAreas] = useState([]);

  // this method maintain the selection when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      cpName: channelPartnerDetails?.name,
      typeOfFirm: channelPartnerDetails?.typeOfFirm,
      primaryEmail: channelPartnerDetails?.primaryEmail,
      primaryMobile: channelPartnerDetails?.primaryMobile,
      officeAddress: {
        addressLine1: channelPartnerDetails?.officeAddress?.addressLine1,
        addressLine2: channelPartnerDetails?.officeAddress?.addressLine2,
        landmark: channelPartnerDetails?.officeAddress?.landmark ? channelPartnerDetails?.officeAddress?.landmark : '',
        pincode: channelPartnerDetails?.officeAddress?.pincode,
        city: channelPartnerDetails?.officeAddress?.city,
        state: channelPartnerDetails?.officeAddress?.state,
        area: channelPartnerDetails?.officeAddress?.area
      },
      branchManagerName: channelPartnerDetails?.managementDetails?.name,
      branchManagerEmail: channelPartnerDetails?.managementDetails?.email,
      branchManagerNumber: channelPartnerDetails?.managementDetails?.mobileNumber,
      submit: null
    },
    validationSchema: Yup.object().shape({
      cpName: Yup.string().max(255).required('Channel Partner Name is Required').matches(new RegExp(stringRegExp),
        'Only alphabets and spaces allowed'),
      typeOfFirm: Yup.string().required('Type of Firm is required'),
      primaryEmail: Yup.string().email('Invalid email').max(255).required('Primary Email is required'),
      primaryMobile: Yup.string().matches(phoneRegExp, 'Phone Number is not valid').required('Primary Mobile Number is required'),
      officeAddress: Yup.object().shape({
        addressLine1: Yup.string().required('Address Line 1 is required'),
        addressLine2: Yup.string().required('Address Line 2 is required'),
        landmark: Yup.string().optional(),
        area: Yup.string().required('Area is required'),
        pincode: Yup.number().moreThan(0).min(100000, 'Not a valid Pincode')
          .max(999999, 'Not a valid Pincode')
          .required('Valid Pincode is Required')
          .typeError('Valid Pincode is Required'),
        city: Yup.string().required('City is required'),
      }),
      branchManagerName: Yup.string().max(255).required('Branch Manager Name is Required').matches(new RegExp(stringRegExp),
        'Only alphabets and spaces allowed'),
      branchManagerEmail: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
      branchManagerNumber: Yup.string().matches(phoneRegExp, 'Mobile Number is not valid').required('Manager Mobile Number is required'),
    }),

    onSubmit: async (values, helpers) => {
      const editChannelPartner: ChannelPartner = {
        name: values.cpName,
        typeOfFirm: values.typeOfFirm,
        primaryEmail: values.primaryEmail,
        primaryMobile: values.primaryMobile,
        officeAddress: {
          city: values.officeAddress.city,
          addressLine1: values.officeAddress.addressLine1,
          addressLine2: values.officeAddress.addressLine2,
          landmark: values.officeAddress.landmark,
          pincode: values.officeAddress.pincode,
          area: values.officeAddress.area,
        },
        managementDetails: {
          name: values.branchManagerName,
          email: values.branchManagerEmail,
          mobileNumber: values.branchManagerNumber,
        }
      };
      helpers.resetForm();
      const resultStatus = await updateBasicDetails(editChannelPartner);
      if (resultStatus) {
        closeDialog('', '');
      }
    }
  });

  const setPincodeDetails = (pincodes) => {
    // Set city
    formik.setFieldValue('officeAddress.city', pincodes[0]?.city);
    // set areas for dropdown
    const areaList = [];
    // get areas from each pincode onject
    pincodes?.forEach((pincode) => areaList.push(pincode.area));
    // set areas using distinct values from areaList
    setAreas([...new Set(areaList)]);
  };

  const getPincodeData = async (pincode: string | number) => {
    const paginationObject: PaginationAndFilter = {
      filters: [
        {
          property: 'code',
          operator: 'equal',
          value: [
            pincode,
          ],
        },
      ],
      pageSize: 9999,
      pageNumber: 1,
      sort: -1,
      sortBy: ''
    };
    const result = await pincodeApi.getPincodesByPaginationAndFilters(paginationObject);
    if (result.responseData.totalRecords) {
      setPincodeDetails(result.responseData.records);
    }
    return result.responseData.records;
  };

  const handlePincodeChange = async (event) => {
    formik.setFieldValue('officeAddress.pincode', event.target.value);
    if (pincodeRegex.test(event.target.value?.toString())) {
      await formik.setFieldTouched('officeAddress.pincode', true);
      const result = await getPincodeData(event.target.value);
      if (result.length) {
        formik.handleChange(event);
        setCheckPincode(false);
        await formik.setFieldError('officeAddress.pincode', undefined);
      } else {
        setCheckPincode(true);
        await formik.setFieldError('officeAddress.pincode', 'Unavailable Pincode');
        setAreas([]);
        formik.setFieldValue('officeAddress.city', '');
        formik.setFieldValue('officeAddress.area', '');
        formik.setFieldTouched('officeAddress.area', false);
      }
    } else {
      await formik.handleChange(event);
      await formik.setFieldTouched('officeAddress.pincode', false);
      setAreas([]);
      formik.setFieldValue('officeAddress.city', '');
      formik.setFieldValue('officeAddress.area', '');
      formik.setFieldTouched('officeAddress.area', false);
    }
  };

  useEffect(() => {
    getPincodeData(channelPartnerDetails?.officeAddress?.pincode);
  }, [channelPartnerDetails?.officeAddress?.pincode]);

  const handleEmailExist = async (event) => {
    const enteredEmail = event.target.value?.trim();
    formik.setFieldValue('primaryEmail', event.target.value);
    if (enteredEmail !== '' && emailRegex.test(enteredEmail) && !formik.errors.primaryEmail) {
      await formik.setFieldTouched('primaryEmail', true);
      const response = await channelPartnerApi.checkCPExistByEmail(enteredEmail);
      if (response?.responseData && response?.responseData?.id !== channelPartnerDetails?.id
        && !response?.responseData?.message) {
        setIsEmailExist(true);
        await formik.setFieldError('primaryEmail', 'This email is already in use');
      } else {
        setIsEmailExist(false);
        await formik.setFieldError('primaryEmail', undefined);
      }
    } else {
      await formik.setFieldTouched('primaryEmail', true);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle
        sx={{ justifyContent: 'flex-end' }}
      >
        Edit Basic Details
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => {
            formik.resetForm();
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ justifyItems: 'center' }}
      >
        <Grid
          container
          sx={{
            mt: 1,
          }}
        >
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.cpName
                      && formik.errors.cpName)}
                fullWidth
                helperText={formik.touched.cpName
                      && formik.errors.cpName}
                placeholder="Channel Partner Name"
                name="cpName"
                required
                disabled
                label="Channel Partner Name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.cpName}
              />
            </Grid>

            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.primaryEmail
                        && formik.errors.primaryEmail)}
                fullWidth
                helperText={formik.touched.primaryEmail
                        && formik.errors.primaryEmail}
                placeholder="Enter Email Address"
                name="primaryEmail"
                required
                disabled
                label="Primary Email Id"
                onChange={formik.handleChange}
                onBlur={handleEmailExist}
                sx={{ mb: 1 }}
                value={formik.values.primaryEmail}
              />
              { isEmailExists && !formik.errors.primaryEmail && (
              <Grid
                sx={{
                  ml: '13px',
                  mt: '-9px'
                }}
              >
                <Typography
                  lineHeight={0.5}
                  sx={{
                    fontWeight: '1'
                  }}
                  variant="caption"
                  color="red"
                >
                  This email is already in use
                </Typography>
              </Grid>
              )}
            </Grid>

            <Grid
              item
              md={2}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.primaryMobile
                  && formik.errors.primaryMobile)}
                helperText={formik.touched.primaryMobile && formik.errors.primaryMobile}
                fullWidth
                required
                label="Mobile Number"
                name="primaryMobile"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Mobile Number"
                value={formik.values.primaryMobile}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                    >
                      +91
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
            </Grid>
            <Grid
              item
              md={2}
              sm={12}
              xs={12}
            >
              <>
                <InputField
                  error={Boolean(formik.touched.typeOfFirm && formik.errors.typeOfFirm)}
                  fullWidth
                  helperText={formik.touched.typeOfFirm && formik.errors.typeOfFirm}
                  placeholder="Select Type"
                  name="typeOfFirm"
                  required
                  label="Type of firm"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  select
                  sx={{ mb: 1 }}
                  value={formik.values.typeOfFirm}
                  SelectProps={{ MenuProps }}
                >
                  {typeOfFirms.map((firmType) => (
                    <MenuItem
                      key={firmType}
                      value={firmType}
                    >
                      {firmType}
                    </MenuItem>
                  ))}
                </InputField>
              </>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >

              <InputField
                error={Boolean(formik.touched.branchManagerName
                        && formik.errors.branchManagerName)}
                fullWidth
                helperText={formik.touched.branchManagerName
                        && formik.errors.branchManagerName}
                placeholder="Enter Manager Name"
                name="branchManagerName"
                required
                label="Manager Name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={{ mb: 1 }}
                value={formik.values.branchManagerName}
              />

            </Grid>
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >

              <InputField
                error={Boolean(formik.touched.branchManagerNumber
                        && formik.errors.branchManagerNumber)}
                helperText={formik.touched.branchManagerNumber
                        && formik.errors.branchManagerNumber}
                fullWidth
                required
                label="Manager Mobile Number"
                name="branchManagerNumber"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Manager Number"
                value={formik.values.branchManagerNumber}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                    >
                      +91
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />

            </Grid>
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >

              <InputField
                error={Boolean(formik.touched.branchManagerEmail
                        && formik.errors.branchManagerEmail)}
                helperText={formik.touched.branchManagerEmail
                        && formik.errors.branchManagerEmail}
                placeholder="Enter Manager Email Id"
                name="branchManagerEmail"
                required
                fullWidth
                label="Manager Email Id"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.branchManagerEmail}
              />

            </Grid>
          </Grid>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.officeAddress?.addressLine1
                        && formik.errors.officeAddress?.addressLine1)}
                fullWidth
                helperText={formik.touched.officeAddress?.addressLine1
                        && formik.errors.officeAddress?.addressLine1}
                placeholder="Enter Address Line 1"
                name="officeAddress.addressLine1"
                required
                label="Address Line 1"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.officeAddress?.addressLine1}
              />
            </Grid>
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.officeAddress?.addressLine2
                        && formik.errors.officeAddress?.addressLine2)}
                fullWidth
                helperText={formik.touched.officeAddress?.addressLine2
                        && formik.errors.officeAddress?.addressLine2}
                placeholder="Enter Address Line 2"
                name="officeAddress.addressLine2"
                required
                label="Address Line 2"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.officeAddress?.addressLine2}
              />

            </Grid>
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.officeAddress?.landmark
                        && formik.errors.officeAddress?.landmark)}
                fullWidth
                helperText={formik.touched.officeAddress?.landmark
                        && formik.errors.officeAddress?.landmark}
                placeholder="Enter landmark "
                name="officeAddress.landmark"
                label="Landmark"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={{ mb: 1 }}
                value={formik.values.officeAddress?.landmark}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >
              <>
                <InputField
                  error={Boolean(formik.touched.officeAddress?.pincode
                          && formik.errors.officeAddress?.pincode)}
                  fullWidth
                  helperText={formik.touched.officeAddress?.pincode
                          && formik.errors.officeAddress?.pincode}
                  placeholder="Enter pincode"
                  name="officeAddress.pincode"
                  required
                  label="Pincode"
                  onBlur={formik.handleBlur}
                  onChange={handlePincodeChange}
                  sx={{ mb: 1 }}
                  value={formik.values.officeAddress?.pincode}
                />
                { checkPincode && !formik.errors.officeAddress?.pincode && (
                <Typography
                  sx={{
                    ml: 2
                  }}
                  variant="caption"
                  color="red"
                >
                  Unavailable Pincode
                </Typography>
                )}
              </>
            </Grid>
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.officeAddress?.area
                        && formik.errors.officeAddress?.area)}
                helperText={formik.touched.officeAddress?.area
                        && formik.errors.officeAddress?.area}
                placeholder="Enter Area"
                name="officeAddress.area"
                required
                select
                disabled={!areas?.length}
                label="Area"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                fullWidth
                value={formik.values.officeAddress?.area}
              >
                <MenuItem
                  key="defaultAreas"
                  value={formik.values.officeAddress?.area}
                />
                {areas?.map((area) => (
                  <MenuItem
                    key={area}
                    value={area}
                  >
                    {area}
                  </MenuItem>
                ))}
              </InputField>
            </Grid>
            <Grid
              item
              md={4}
              sm={12}
              xs={12}
            >

              <InputField
                error={Boolean(formik.touched.officeAddress?.city
                        && formik.errors.officeAddress?.city)}
                helperText={formik.touched.officeAddress?.city
                        && formik.errors.officeAddress?.city}
                fullWidth
                required
                name="officeAddress.city"
                label="City"
                disabled
                placeholder="Enter city"
                value={formik.values.officeAddress?.city}
                sx={{ mb: 1 }}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: 'right'
        }}
      >
        <Button
          color="primary"
          onClick={(e) => {
            formik.resetForm();
            closeDialog(e, '');
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={(!(formik.isValid && formik.dirty) || formik.isSubmitting)
            || isEmailExists || checkPincode}
          color="primary"
          onClick={() => { formik.handleSubmit(); }}
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditBasicInfo.defaultProps = {
  open: false,
};

EditBasicInfo.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  // @ts-ignore
  channelPartnerDetails: PropTypes.object.isRequired,
  updateBasicDetails: PropTypes.func
};
